import { supabase } from '../lib/supabase';

// ── Product Profile — Linked Partners ──────────────────────────────────────

export const productLinkedPartnersService = {
  async getByProduct(catalogProductId) {
    const { data, error } = await supabase
      ?.from('partner_products')
      ?.select(`
        id,
        partner_id,
        margin_pct,
        authorization_level,
        is_strategic,
        link_status,
        notes,
        created_at,
        partners (
          id, legal_name, brand_name, tier, status, country
        )
      `)
      ?.eq('catalog_product_id', catalogProductId)
      ?.order('created_at', { ascending: false });
    if (error) throw error;
    return (data || [])?.map(row => ({
      id: row?.id,
      partnerId: row?.partner_id,
      marginPct: row?.margin_pct,
      authorizationLevel: row?.authorization_level,
      isStrategic: row?.is_strategic,
      linkStatus: row?.link_status,
      notes: row?.notes,
      createdAt: row?.created_at,
      partnerName: row?.partners?.brand_name || row?.partners?.legal_name || '—',
      partnerTier: row?.partners?.tier,
      partnerStatus: row?.partners?.status,
      partnerCountry: row?.partners?.country,
    }));
  },

  async unlink(linkId) {
    const { error } = await supabase?.from('partner_products')?.delete()?.eq('id', linkId);
    if (error) throw error;
  },

  async countByProduct(catalogProductId) {
    const { count, error } = await supabase
      ?.from('partner_products')
      ?.select('id', { count: 'exact', head: true })
      ?.eq('catalog_product_id', catalogProductId);
    if (error) throw error;
    return count || 0;
  },
};

// ── Product Lifecycle ───────────────────────────────────────────────────────

export const productLifecycleService = {
  async getByProduct(catalogProductId) {
    const { data, error } = await supabase
      ?.from('product_lifecycle')
      ?.select(`
        id, stage, stage_date, notes, expected_eol_date, created_at,
        user_profiles ( full_name, email )
      `)
      ?.eq('catalog_product_id', catalogProductId)
      ?.order('stage_date', { ascending: true });
    if (error) throw error;
    return (data || [])?.map(row => ({
      id: row?.id,
      stage: row?.stage,
      stageDate: row?.stage_date,
      notes: row?.notes,
      expectedEolDate: row?.expected_eol_date,
      createdAt: row?.created_at,
      changedByName: row?.user_profiles?.full_name || row?.user_profiles?.email || 'System',
    }));
  },

  async addStage(catalogProductId, stageData) {
    const { data: { user } } = await supabase?.auth?.getUser();
    const { data, error } = await supabase
      ?.from('product_lifecycle')
      ?.insert({
        catalog_product_id: catalogProductId,
        stage: stageData?.stage,
        stage_date: stageData?.stageDate || new Date()?.toISOString()?.split('T')?.[0],
        notes: stageData?.notes || null,
        expected_eol_date: stageData?.expectedEolDate || null,
        changed_by: user?.id || null,
      })
      ?.select()
      ?.single();
    if (error) throw error;
    return data;
  },
};

// ── Product Updates / News Feed ─────────────────────────────────────────────

export const productUpdatesService = {
  async getByProduct(catalogProductId) {
    const { data, error } = await supabase
      ?.from('product_updates')
      ?.select(`
        id, update_type, title, content, created_at, updated_at,
        user_profiles ( full_name, email )
      `)
      ?.eq('catalog_product_id', catalogProductId)
      ?.order('created_at', { ascending: false });
    if (error) throw error;
    return (data || [])?.map(row => ({
      id: row?.id,
      updateType: row?.update_type,
      title: row?.title,
      content: row?.content,
      createdAt: row?.created_at,
      updatedAt: row?.updated_at,
      authorName: row?.user_profiles?.full_name || row?.user_profiles?.email || 'Admin',
    }));
  },

  async create(catalogProductId, updateData) {
    const { data: { user } } = await supabase?.auth?.getUser();
    const { data, error } = await supabase
      ?.from('product_updates')
      ?.insert({
        catalog_product_id: catalogProductId,
        update_type: updateData?.updateType || 'general',
        title: updateData?.title,
        content: updateData?.content || null,
        posted_by: user?.id || null,
      })
      ?.select(`id, update_type, title, content, created_at, user_profiles(full_name, email)`)
      ?.single();
    if (error) throw error;
    return {
      id: data?.id,
      updateType: data?.update_type,
      title: data?.title,
      content: data?.content,
      createdAt: data?.created_at,
      authorName: data?.user_profiles?.full_name || data?.user_profiles?.email || 'Admin',
    };
  },

  async delete(id) {
    const { error } = await supabase?.from('product_updates')?.delete()?.eq('id', id);
    if (error) throw error;
  },
};
